import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { FileSpreadsheet, FileText, Loader2, Search } from 'lucide-react';
import { OrgPortalApi } from '@/services/org.api';
import { OrgReportsApi } from '@/services/org-reports.api';
import type { ExportFormat } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Input, Select } from '@/components/ui/Input';

/**
 * Students Attendance Summary — org tier, read-only. Per-school rows x
 * per-class present/absent/total for one selected date, matching the real
 * `state-reports.school-attendance-report` shape. `absent` here means
 * "not marked present that day" (total enrolled minus present), same
 * formula real legacy uses — not an explicit absent mark. Grade columns
 * are dynamic (this project's real classes.name values), not legacy's
 * hardcoded single-reference-school class list.
 */
export function OrgStudentsAttendanceSummaryPage() {
  const [schoolId, setSchoolId] = useState('');
  const [stateId, setStateId] = useState<number | ''>('');
  const [status, setStatus] = useState<'active' | 'inactive' | 'all'>('active');
  const [attendanceType, setAttendanceType] = useState<'' | '1' | '0'>('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [search, setSearch] = useState('');
  const [date, setDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [busy, setBusy] = useState<ExportFormat | null>(null);

  const { data: schools = [] } = useQuery({
    queryKey: ['schools-for-org-students-attendance-summary'],
    queryFn: async () => (await OrgPortalApi.listSchools({ limit: 100 })).items,
  });

  const { data: states = [] } = useQuery({
    queryKey: ['org-report-states'],
    queryFn: () => OrgReportsApi.listStates(),
  });

  const params = {
    schoolId: schoolId || undefined,
    date,
    stateId: stateId === '' ? undefined : stateId,
    status,
    attendanceType: attendanceType || undefined,
    showDeleted: showDeleted || undefined,
    search: search.trim() || undefined,
  };

  const { data: rows = [], isFetching } = useQuery({
    queryKey: ['org-students-attendance-summary', params],
    queryFn: () => OrgReportsApi.studentsAttendanceSummary(params),
  });

  const classColumns = [...new Set(rows.flatMap((r) => r.classes.map((c) => c.className)))].sort();

  const runExport = async (format: ExportFormat) => {
    if (busy) return;
    setBusy(format);
    try {
      await OrgReportsApi.exportStudentsAttendanceSummary(format, params);
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <PageHeader
        title="Students Attendance Summary"
        description="Present/absent/total per class, per school, for one selected date."
        actions={
          <div className="inline-flex overflow-hidden rounded-md border border-slate-200">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('xlsx')}
              disabled={!!busy}
              title="Export to Excel"
            >
              {busy === 'xlsx' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-green-600" />}
              Excel
            </button>
            <div className="w-px bg-slate-200" />
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('pdf')}
              disabled={!!busy}
              title="Export to PDF"
            >
              {busy === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-red-600" />}
              PDF
            </button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by school name, email, phone…"
            className="w-64 pl-9"
          />
        </div>
        <Select
          className="!w-48"
          value={stateId}
          onChange={(e) => setStateId(e.target.value === '' ? '' : Number(e.target.value))}
        >
          <option value="">All states</option>
          {states.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <Select className="!w-56" value={schoolId} onChange={(e) => setSchoolId(e.target.value)}>
          <option value="">All schools</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <Select className="!w-36" value={status} onChange={(e) => setStatus(e.target.value as 'active' | 'inactive' | 'all')}>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="all">All</option>
        </Select>
        <Select className="!w-40" value={attendanceType} onChange={(e) => setAttendanceType(e.target.value as '' | '1' | '0')}>
          <option value="">Present &amp; Absent</option>
          <option value="1">Present only</option>
          <option value="0">Absent only</option>
        </Select>
        <Input type="date" className="!w-40" value={date} onChange={(e) => setDate(e.target.value)} />
        <label className="flex items-center gap-1.5 text-sm text-slate-600">
          <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} />
          Show Deleted
        </label>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">School</th>
                {classColumns.map((c) => (
                  <th key={c} colSpan={3} className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {c}
                  </th>
                ))}
              </tr>
              <tr>
                <th className="px-4 py-1"></th>
                {classColumns.map((c) => (
                  <Fragment key={c}>
                    <th className="px-2 py-1 text-right text-xs text-slate-400" title="Total">T</th>
                    <th className="px-2 py-1 text-right text-xs text-green-600" title="Present">P</th>
                    <th className="px-2 py-1 text-right text-xs text-red-600" title="Absent">A</th>
                  </Fragment>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {isFetching && !rows.length ? (
                <tr>
                  <td colSpan={1 + classColumns.length * 3} className="px-4 py-12 text-center text-slate-400">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={1 + classColumns.length * 3} className="px-4 py-12 text-center text-slate-400">
                    No schools found.
                  </td>
                </tr>
              ) : (
                rows.map((r) => {
                  const byClass = new Map(r.classes.map((c) => [c.className, c]));
                  return (
                    <tr key={r.schoolId} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-800">{r.schoolName}</td>
                      {classColumns.map((c) => {
                        const cell = byClass.get(c);
                        return (
                          <Fragment key={c}>
                            <td className="px-2 py-3 text-right text-slate-700">{cell?.total ?? '—'}</td>
                            <td className="px-2 py-3 text-right text-green-700">{cell?.present ?? '—'}</td>
                            <td className="px-2 py-3 text-right text-red-700">{cell?.absent ?? '—'}</td>
                          </Fragment>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
